import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReportModal from '../components/ReportModal';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, Clock, PlayCircle, FilePlus, Newspaper } from 'lucide-react';
import { Button, BypassLayout, ExamCard, DesktopPagination } from '../components/common';
import { useDesktopExpanded } from '../hooks/useDesktopExpanded';
import '../styles/desktop-expanded.css';

function ExamList() {
  const { getAccessToken, userType } = useAuth();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [examList, setExamList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchStudentId, setSearchStudentId] = useState(searchParams.get('student_id') || '');
  const [searchStudentName, setSearchStudentName] = useState(searchParams.get('student_name') || '');
  const [searchExamId, setSearchExamId] = useState(searchParams.get('exam_id') || '');
  const [searchSchoolName, setSearchSchoolName] = useState(searchParams.get('school_name') || '');
  const [searchTeacherName, setSearchTeacherName] = useState(searchParams.get('teacher_name') || '');
  const [activeFilter, setActiveFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [isTeachersLoading, setIsTeachersLoading] = useState(false);
  
  // 데스크톱 확장 훅
  const { isMobileView, shouldUsePagination } = useDesktopExpanded();

  // 선생님 목록 로딩
  const fetchTeachers = async () => {
    try {
      setIsTeachersLoading(true);
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/teacher?page=1&per_page=50`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );
      if (!response.ok) throw new Error('강사 목록을 불러오는데 실패했습니다.');
      const result = await response.json();
      const filteredTeachers = result.data?.filter(teacher =>
        ['원장', '강사', '팀장'].includes(teacher.charge_work)
      ) || [];
      setTeachers(filteredTeachers);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    } finally {
      setIsTeachersLoading(false);
    }
  };

  useEffect(() => {
    if (userType === 'manager') {
      fetchTeachers();
    }
  }, [userType]);

  // 화면 크기에 따라 표시할 수 있는 행 수 계산
  const calculateItemsPerPage = () => {
    try {
      const windowHeight = window.innerHeight;
      const buffer = 10;

      let availableHeight, rowHeight;

      if (isMobileView) {
        // 모바일: 카드 방식이므로 넉넉하게
        return 20;
      } else {
        // 데스크톱: 카드 높이 calc(100vh - 6rem) = windowHeight - 96px
        const cardHeight = windowHeight - 96;
        // 카드 내부 고정 요소:
        // - padding(32) + 타이틀(28) + 필터(80) + marginTop(16) + 테이블헤더(40) + 페이지네이션(60)
        const cardInternalFixed = 32 + 28 + 80 + 16 + 40 + 60 + buffer;
        availableHeight = cardHeight - cardInternalFixed;
        rowHeight = 48;
      }

      const itemsCount = Math.floor(availableHeight / rowHeight);
      const minItems = 5;
      const maxItems = 50;

      return Math.max(minItems, Math.min(itemsCount, maxItems));
    } catch (error) {
      console.error('Error calculating items per page:', error);
      return 10;
    }
  };

  // report_status에 따른 아이콘 색상과 툴팁을 반환하는 함수
  const getReportStatusInfo = (status) => {
    switch (status) {
      case 0:
        return { color: 'text-gray-400', tooltip: '리포트 없음' };
      case 100:
        return { color: 'text-warning', tooltip: '생성 요청됨' };
      case 101:
        return { color: 'text-orange-500', tooltip: '생성 중' };
      case 102:
        return { color: 'text-danger', tooltip: '생성 오류' };
      case 150:
        return { color: 'text-purple-500', tooltip: '편집 중' };
      case 200:
      case 998:
        return { color: 'text-blue-500', tooltip: '준비 완료(미확정)' };
      case 999:
        return { color: 'text-success', tooltip: '확정 완료' };
      default:
        return { color: 'text-gray-400', tooltip: '상태 불명' };
    }
  };

  const openModal = (exam) => {
    setSelectedExam(exam);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedExam(null);
  };

  // 데이터 로딩 함수
  const fetchExams = async (page = 1, append = false, filterType = null) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const perPage = calculateItemsPerPage();
      let url = `${process.env.REACT_APP_API_BASE_URL}/api/exams/list?page=${page}&per_page=${perPage}`;
      
      // 필터가 활성화된 경우 필터 API 사용
      // filterType이 명시적으로 전달된 경우 그것을 사용, null이면 필터 없음
      const currentFilter = filterType !== undefined ? filterType : activeFilter;
      
      // null이 명시적으로 전달되거나 필터가 없으면 기본 API 사용
      if (currentFilter && currentFilter !== null) {
        let param = '';
        switch (currentFilter) {
          case 'student_id':
            param = `student_id=${searchStudentId}`;
            break;
          case 'student_name':
            param = `student_name=${searchStudentName}`;
            break;
          case 'exam_id':
            param = `exam_id=${searchExamId}`;
            break;
          case 'school_name':
            param = `school_name=${searchSchoolName}`;
            break;
          case 'teacher_name':
            param = `teacher_name=${searchTeacherName}`;
            break;
        }
        url = `${process.env.REACT_APP_API_BASE_URL}/api/exams/list/filtered?${param}&page=${page}&per_page=${perPage}`;
      }

      const response = await fetch(
        url,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 404) {
        if (append) {
          setHasMore(false);
        } else {
          setExamList([]);
          setTotalItems(0);
          setHasMore(false);
        }
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch exams');
      }

      const data = await response.json();
      if (data.status === 'success') {
        const newExams = data.data || [];
        // DEBUG: API 응답 구조 확인 (student_info에 lecture 필드 포함 여부)
        // if (newExams.length > 0) console.log('[ExamList] 첫 번째 exam 객체:', JSON.stringify(newExams[0], null, 2));
        const totalItems = Number(data.total_items);
        
        if (append) {
          setExamList(prev => [...prev, ...newExams]);
        } else {
          setExamList(newExams);
          setTotalItems(totalItems);
        }
        
        // 더 불러올 데이터가 있는지 확인
        const currentTotal = append ? (page * perPage) : newExams.length;
        setHasMore(currentTotal < totalItems);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // 더 보기 함수
  const loadMore = () => {
    if (!isLoadingMore && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchExams(nextPage, true, activeFilter);
    }
  };

  // URL 파라미터가 변경될 때마다 (뒤로/앞으로가기 포함) 상태 복원 및 데이터 로딩
  useEffect(() => {
    const studentId = searchParams.get('student_id');
    const studentName = searchParams.get('student_name');
    const examId = searchParams.get('exam_id');
    const schoolName = searchParams.get('school_name');
    const teacherName = searchParams.get('teacher_name');
    const pageParam = parseInt(searchParams.get('page')) || 1;

    // URL에서 상태 복원
    setSearchStudentId(studentId || '');
    setSearchStudentName(studentName || '');
    setSearchExamId(examId || '');
    setSearchSchoolName(schoolName || '');
    setSearchTeacherName(teacherName || '');
    setCurrentPage(pageParam);

    let initialFilter = '';
    if (studentId) initialFilter = 'student_id';
    else if (studentName) initialFilter = 'student_name';
    else if (examId) initialFilter = 'exam_id';
    else if (schoolName) initialFilter = 'school_name';
    else if (teacherName) initialFilter = 'teacher_name';

    setActiveFilter(initialFilter);

    if (initialFilter) {
      fetchExams(pageParam, false, initialFilter);
    } else {
      fetchExams(pageParam, false, null);
    }
  }, [searchParams]);

  // 필터가 변경될 때 데이터 재로딩 (초기 로드 시에만)
  // handleSearch에서 직접 fetchExams를 호출하므로 주석 처리
  // useEffect(() => {
  //   if (activeFilter) {
  //     fetchExams(1, false);
  //   }
  // }, [activeFilter]);



  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        handleReset();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  // 스크롤 이벤트 핸들러 (모바일에서만 작동)
  const handleScroll = (e) => {
    try {
      // 데스크톱에서는 페이지네이션 사용
      if (!isMobileView) return;
      
      if (isLoadingMore || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = e.target;
      
      // 스크롤이 하단 100px 이내에 도달하면 다음 페이지 로드
      const threshold = 1500;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
      
      if (isNearBottom) {
        loadMore();
      }
    } catch (error) {
      console.error('Error in scroll handler:', error);
    }
  };
  
  // 데스크톱 페이지 변경 핸들러 - URL에 page 파라미터 포함
  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage);
    navigate(`/student/exam-list?${params.toString()}`);
  };







  const formatDate = (dateString) => {
    const date = new Date(dateString);
    date.setHours(date.getHours() + 9);

    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDifficulty = (difficulty) => {
    return Math.floor(difficulty / 100 * 100) / 100;
  };

  const handleExamClick = (student_id, stamp_id, exam_id) => {
    navigate(`/student/${student_id}/stamp/${stamp_id}/result/${exam_id}`);
  };

  const handleSearch = async (type) => {
    // 검색 타입별 유효성 검사
    switch (type) {
      case 'student_id':
        if (!searchStudentId) return;
        break;
      case 'student_name':
        if (!searchStudentName || !/^[A-Za-z가-힣0-9]{2,10}$/.test(searchStudentName)) {
          alert('학생 이름은 2~10자의 한글, 영문, 숫자만 가능합니다.');
          return;
        }
        break;
      case 'exam_id':
        if (!searchExamId) return;
        break;
      case 'school_name':
        if (!searchSchoolName) return;
        break;
      case 'teacher_name':
        if (!searchTeacherName) return;
        break;
      default:
        return;
    }

    // 상태 초기화
    setCurrentPage(1);
    setExamList([]);
    setHasMore(true);
    
    // activeFilter 설정
    setActiveFilter(type);
    
    // URL 업데이트 - page=1 포함, navigate로 히스토리에 push
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('page', '1');
    if (type === 'student_id' && searchStudentId) {
      newSearchParams.set('student_id', searchStudentId);
    } else if (type === 'student_name' && searchStudentName) {
      newSearchParams.set('student_name', searchStudentName);
    } else if (type === 'exam_id' && searchExamId) {
      newSearchParams.set('exam_id', searchExamId);
    } else if (type === 'school_name' && searchSchoolName) {
      newSearchParams.set('school_name', searchSchoolName);
    } else if (type === 'teacher_name' && searchTeacherName) {
      newSearchParams.set('teacher_name', searchTeacherName);
    }
    navigate(`/student/exam-list?${newSearchParams.toString()}`);
  };

  const handleKeyDown = (e, type) => {
    if (e.key === 'Enter') {
      handleSearch(type);
    }
  };

  const handleReset = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    // URL 초기화 → useEffect가 상태 복원 및 데이터 로딩 처리
    navigate('/student/exam-list');
  };

  return (
    <>
      <BypassLayout
        loading={isLoading}
        renderContent={({ isMobileView, getSafeAreaStyle }) => (
          <>
            {/* 메인 카드 컨테이너 */}
            <div
              className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${
                isMobileView ? 'flex-1' : 'h-[calc(100vh-6rem)] w-full min-w-0'
              }`}
              style={{
                ...getSafeAreaStyle(),
                width: isMobileView ? 'calc(100% - 28.8px)' : '100%',
                maxWidth: '100%',
                height: isMobileView ? 'calc(100% - 28.8px)' : '100%',
                margin: isMobileView ? '14.4px' : '0'
              }}
            >
              {/* 헤더 영역 */}
              <div className="shrink-0 border-b overflow-hidden">
                <div style={{
                  padding: isMobileView ? '0' : '16px',
                  marginBottom: isMobileView ? '12px' : '0'
                }}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold">테스트관리</h3>
                      <p className="text-xs text-gray-500">
                        - 총 <span className="font-semibold text-primary">{totalItems || 0}</span>개 중 <span className="font-semibold text-primary">{examList?.length || 0}</span>개 표시 중
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                {isMobileView ? (
                  <div className="flex gap-2">
                    <div className="flex-1 space-y-2">
                      <input
                        type="number"
                        value={searchExamId}
                        onChange={(e) => setSearchExamId(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            // 입력된 필드에 따라 적절한 검색 실행
                            if (searchExamId) handleSearch('exam_id');
                            else if (searchStudentId) handleSearch('student_id');
                            else if (searchStudentName) handleSearch('student_name');
                            else if (searchSchoolName) handleSearch('school_name');
                            else if (searchTeacherName) handleSearch('teacher_name');
                          }
                        }}
                        placeholder="TID"
                        className="w-full rounded border px-2 py-1 text-xs"
                      />
                      <input
                        type="number"
                        value={searchStudentId}
                        onChange={(e) => setSearchStudentId(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            // 입력된 필드에 따라 적절한 검색 실행
                            if (searchExamId) handleSearch('exam_id');
                            else if (searchStudentId) handleSearch('student_id');
                            else if (searchStudentName) handleSearch('student_name');
                            else if (searchSchoolName) handleSearch('school_name');
                            else if (searchTeacherName) handleSearch('teacher_name');
                          }
                        }}
                        placeholder="학생ID"
                        className="w-full rounded border px-2 py-1 text-xs"
                      />
                      <input
                        type="text"
                        value={searchStudentName}
                        onChange={(e) => setSearchStudentName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            // 입력된 필드에 따라 적절한 검색 실행
                            if (searchExamId) handleSearch('exam_id');
                            else if (searchStudentId) handleSearch('student_id');
                            else if (searchStudentName) handleSearch('student_name');
                            else if (searchSchoolName) handleSearch('school_name');
                            else if (searchTeacherName) handleSearch('teacher_name');
                          }
                        }}
                        placeholder="학생 이름"
                        className="w-full rounded border px-2 py-1 text-xs"
                      />
                      <input
                        type="text"
                        value={searchSchoolName}
                        onChange={(e) => setSearchSchoolName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            // 입력된 필드에 따라 적절한 검색 실행
                            if (searchExamId) handleSearch('exam_id');
                            else if (searchStudentId) handleSearch('student_id');
                            else if (searchStudentName) handleSearch('student_name');
                            else if (searchSchoolName) handleSearch('school_name');
                            else if (searchTeacherName) handleSearch('teacher_name');
                          }
                        }}
                        placeholder="학교 이름"
                        className="w-full rounded border px-2 py-1 text-xs"
                      />
                      {userType === 'manager' && (
                        <input
                          type="text"
                          value={searchTeacherName}
                          onChange={(e) => setSearchTeacherName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              // 입력된 필드에 따라 적절한 검색 실행
                              if (searchExamId) handleSearch('exam_id');
                              else if (searchStudentId) handleSearch('student_id');
                              else if (searchStudentName) handleSearch('student_name');
                              else if (searchSchoolName) handleSearch('school_name');
                              else if (searchTeacherName) handleSearch('teacher_name');
                            }
                          }}
                          placeholder="담당 강사"
                          className="w-full rounded border px-2 py-1 text-xs"
                        />
                      )}
                    </div>
                    <div className="flex w-16 flex-col gap-2">
                      <Button
                        onClick={handleReset}
                        variant="secondary"
                        size="sm"
                        rounded="3xl"
                        className="h-full min-h-[44px]"
                      >
                        초기화
                      </Button>
                      <Button
                        onClick={() => {
                          // 입력된 필드에 따라 적절한 검색 실행
                          if (searchExamId) handleSearch('exam_id');
                          else if (searchStudentId) handleSearch('student_id');
                          else if (searchStudentName) handleSearch('student_name');
                          else if (searchSchoolName) handleSearch('school_name');
                          else if (searchTeacherName) handleSearch('teacher_name');
                        }}
                        variant="primary"
                        size="sm"
                        rounded="3xl"
                        className="h-full min-h-[44px]"
                      >
                        조회
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-nowrap items-center gap-1.5 min-w-0 w-full overflow-hidden">
                    <div className="flex flex-1 min-w-0 items-center gap-2">
                      <input
                        type="number"
                        value={searchExamId}
                        onChange={(e) => setSearchExamId(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, 'exam_id')}
                        placeholder="TID"
                        className="min-w-0 flex-1 rounded-3xl border px-2 py-1.5 text-sm"
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSearch('exam_id')}
                      >
                        검색
                      </Button>
                    </div>
                    <div className="flex flex-1 min-w-0 items-center gap-2">
                      <input
                        type="number"
                        value={searchStudentId}
                        onChange={(e) => setSearchStudentId(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, 'student_id')}
                        placeholder="학생ID"
                        className="min-w-0 flex-1 rounded-3xl border px-2 py-1.5 text-sm"
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSearch('student_id')}
                      >
                        검색
                      </Button>
                    </div>
                    <div className="flex flex-1 min-w-0 items-center gap-2">
                      <input
                        type="text"
                        value={searchStudentName}
                        onChange={(e) => setSearchStudentName(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, 'student_name')}
                        placeholder="학생 이름"
                        className="min-w-0 flex-1 rounded-3xl border px-2 py-1.5 text-sm"
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSearch('student_name')}
                      >
                        검색
                      </Button>
                    </div>
                    <div className="flex flex-1 min-w-0 items-center gap-2">
                      <input
                        type="text"
                        value={searchSchoolName}
                        onChange={(e) => setSearchSchoolName(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, 'school_name')}
                        placeholder="학교 이름"
                        className="min-w-0 flex-1 rounded-3xl border px-2 py-1.5 text-sm"
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSearch('school_name')}
                      >
                        검색
                      </Button>
                    </div>
                    {userType === 'manager' && (
                      <div className="flex flex-1 min-w-0 items-center">
                        <select
                          value={searchTeacherName}
                          onChange={(e) => {
                            setSearchTeacherName(e.target.value);
                            if (e.target.value) {
                              setCurrentPage(1);
                              setExamList([]);
                              setHasMore(true);
                              setActiveFilter('teacher_name');
                              const params = new URLSearchParams();
                              params.set('page', '1');
                              params.set('teacher_name', e.target.value);
                              navigate(`/student/exam-list?${params.toString()}`);
                            } else {
                              navigate('/student/exam-list');
                            }
                          }}
                          className="min-w-0 flex-1 rounded-3xl border px-2 py-1.5 text-sm bg-white"
                          disabled={isTeachersLoading}
                        >
                          <option value="">
                            {isTeachersLoading ? '로딩중...' : '담당 강사'}
                          </option>
                          {teachers.map((teacher) => (
                            <option key={teacher.user_id} value={teacher.user_name}>
                              {teacher.user_name} ({teacher.charge_work})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleReset}
                      title="ESC를 눌러서 초기화"
                      className="shrink-0"
                    >
                      초기화
                    </Button>
                  </div>
                )}
                  </div>
                </div>
              </div>

              {/* 컨텐츠 영역 */}
              {isMobileView ? (
                /* 모바일: 카드 레이아웃 */
                <div className="flex-1 overflow-y-auto bg-gray-50" onScroll={handleScroll}>
                  <div className="p-3">
                    {isLoading && examList.length === 0 ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="mr-2 size-6 animate-spin rounded-full border-b-2 border-primary"></div>
                        <span className="text-xs text-gray-500">로딩 중...</span>
                      </div>
                    ) : examList.length === 0 ? (
                      <div className="py-8 text-center">
                        <p className="text-sm font-medium text-gray-500">테스트 데이터가 없습니다</p>
                        <p className="mt-1 text-xs text-gray-400">검색 조건을 변경하거나 초기화해보세요</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3">
                        {examList.map((exam) => (
                          <ExamCard
                            key={exam.exam_id}
                            exam={exam}
                            onExamClick={handleExamClick}
                            onReportClick={openModal}
                            getReportStatusInfo={getReportStatusInfo}
                            formatDate={formatDate}
                            formatDifficulty={formatDifficulty}
                          />
                        ))}
                      </div>
                    )}

                    {/* 하단 로딩/완료 메시지 */}
                    {examList.length > 0 && (
                      <div className="mt-6">
                        {isLoadingMore ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="mr-2 size-6 animate-spin rounded-full border-b-2 border-primary"></div>
                            <span className="text-xs text-gray-500">더 불러오는 중...</span>
                          </div>
                        ) : !hasMore ? (
                          <div className="flex items-center justify-center py-4">
                            <span className="text-xs text-gray-500">모든 테스트를 불러왔습니다.</span>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* 데스크톱: 테이블 레이아웃 (학생관리 동일 구조) */
                <div className="flex-1 overflow-y-auto min-w-0" style={{
                  padding: '0',
                  marginTop: '16px',
                  maxHeight: 'calc(100vh - 14rem)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}>
                  {isLoading ? (
                    <div className="py-4 text-center">로딩 중...</div>
                  ) : (
                    <div className="overflow-x-auto flex-1 min-w-0" style={{ minHeight: 0 }}>
                      <table className="w-full table-fixed" style={{ minWidth: '450px' }}>
                        <colgroup>
                          <col style={{ width: '19%' }} />   {/* 학생 */}
                          <col style={{ width: '11%' }} />   {/* 담당/강의 */}
                          <col style={{ width: '16%' }} />   {/* 과목 */}
                          <col style={{ width: '15%' }} />   {/* 상태/TID */}
                          <col style={{ width: '7%' }} />    {/* 난이도 */}
                          <col style={{ width: '6%' }} />    {/* 문제수 */}
                          <col style={{ width: '8%' }} />    {/* 정답률 */}
                          <col style={{ width: '18%' }} />   {/* 일시 */}
                        </colgroup>
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">학생</th>
                            <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">담당/강의</th>
                            <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">과목</th>
                            <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">상태/TID</th>
                            <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">난이도</th>
                            <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">문제수</th>
                            <th className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">정답률</th>
                            <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">일시</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {examList.map((exam) => {
                            const teacherName = exam.student_info?.teacher_name || exam.student_info?.in_charge_teacher;
                            const totalCount = exam.length || ((exam.correct_count || 0) + (exam.wrong_count || 0));
                            const correctRate = totalCount > 0
                              ? Math.round(((exam.correct_count || 0) / totalCount) * 100)
                              : null;
                            const examTypeSymbol = exam.exam_type === 'alpha' ? 'α' :
                              exam.exam_type === 'beta' ? 'β' :
                              exam.exam_type === 'gamma' ? 'γ' :
                              exam.exam_type === 'delta' ? 'δ' :
                              exam.exam_type === 'custom' ? '𝐂' :
                              exam.exam_type === 'epsilon' ? 'ε' :
                              exam.exam_type || '-';

                            return (
                              <tr
                                key={exam.exam_id}
                                onClick={() => handleExamClick(exam.student_info.student_id, exam.stamp_id, exam.exam_id)}
                                className="hover:bg-primary/10 cursor-pointer transition-colors duration-150 border-b border-gray-100"
                              >
                                {/* 학생 */}
                                <td className="px-1.5 py-2 overflow-hidden rounded-l-3xl transition-colors duration-150">
                                  <div className="flex items-center min-w-0">
                                    <div className="flex-shrink-0 h-8 w-8">
                                      <div
                                        className="h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-sm bg-gradient-to-r from-primary to-primary/70"
                                        title={`학생 ID: ${exam.student_info.student_id}`}
                                      >
                                        {exam.student_info.student_id}
                                      </div>
                                    </div>
                                    <div className="ml-2 min-w-0">
                                      <div className="text-sm font-medium text-gray-900 truncate leading-tight">{exam.student_info.student_name}</div>
                                      <div className="text-xs text-gray-500 truncate leading-tight">{exam.student_info.school_name} · {exam.student_info.school_grade}학년</div>
                                    </div>
                                  </div>
                                </td>
                                {/* 담당/강의 */}
                                <td className="px-1.5 py-2 overflow-hidden transition-colors duration-150">
                                  <div className="text-sm text-gray-900 truncate">{teacherName || '-'}</div>
                                  {(exam.student_info?.lecture || exam.lecture) && (
                                    <div className="text-xs text-gray-500 truncate">({exam.student_info?.lecture || exam.lecture})</div>
                                  )}
                                </td>
                                {/* 과목 */}
                                <td className="px-1.5 py-2 overflow-hidden transition-colors duration-150">
                                  <div className="text-sm text-gray-900 truncate leading-tight">{exam.tree_name}</div>
                                  <div className="text-xs text-gray-500 truncate leading-tight">{exam.edu_ver}</div>
                                </td>
                                {/* 상태/TID */}
                                <td className="px-1.5 py-2 overflow-hidden text-center transition-colors duration-150">
                                  <div className="flex items-center justify-center gap-1">
                                    <div className="flex-shrink-0">
                                      {exam.exam_status === 999 ? (
                                        <CheckCircle2 className="text-green-600" size={24} />
                                      ) : exam.exam_status === 400 ? (
                                        <PlayCircle className="text-orange-500" size={24} />
                                      ) : (
                                        <FilePlus className="text-blue-500" size={24} />
                                      )}
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-0.5">
                                        <span className="text-sm font-semibold text-gray-800">{exam.exam_id}</span>
                                        <span className="text-sm font-medium text-green-600">{examTypeSymbol}</span>
                                      </div>
                                      <div className="text-xs text-gray-500">({exam.exam_round || 0}회)</div>
                                    </div>
                                  </div>
                                </td>
                                {/* 난이도 */}
                                <td className="px-1.5 py-2 text-center transition-colors duration-150">
                                  <span className="text-sm font-semibold text-gray-800">
                                    {exam.difficulty ? formatDifficulty(exam.difficulty) : '-'}
                                  </span>
                                </td>
                                {/* 문제수 */}
                                <td className="px-1.5 py-2 text-center transition-colors duration-150">
                                  <span className="text-sm font-semibold text-gray-800">{totalCount}</span>
                                </td>
                                {/* 정답률 */}
                                <td className="px-1.5 py-2 text-center transition-colors duration-150">
                                  <div className="text-sm font-semibold text-green-600">
                                    {correctRate !== null ? `${correctRate}%` : '-'}
                                  </div>
                                  <div className="text-[10px]">
                                    <span className="text-primary font-bold">{exam.correct_count || 0}</span>
                                    <span className="text-gray-400">/</span>
                                    <span className="text-danger font-bold">{exam.wrong_count || 0}</span>
                                    <span className="text-gray-400">/</span>
                                    <span className="text-black font-bold">{totalCount}</span>
                                  </div>
                                </td>
                                {/* 일시 */}
                                <td className="px-1.5 py-2 overflow-hidden rounded-r-3xl transition-colors duration-150">
                                  <div className="text-xs text-gray-500">
                                    <div className="truncate">생성: {exam.exam_created_at ? formatDate(exam.exam_created_at) : '-'}</div>
                                    <div className="truncate">수정: {exam.exam_updated_at ? formatDate(exam.exam_updated_at) : '-'}</div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* 빈 데이터 */}
                  {!isLoading && examList.length === 0 && (
                    <div className="py-8 text-center text-gray-500">
                      테스트 데이터가 없습니다.
                    </div>
                  )}

                  {/* 데스크톱 페이지네이션 */}
                  {!isLoading && examList.length > 0 && shouldUsePagination() && (
                    <div className="shrink-0 bg-white">
                      <DesktopPagination
                        currentPage={currentPage}
                        totalPages={Math.ceil(totalItems / calculateItemsPerPage())}
                        onPageChange={handlePageChange}
                        itemsPerPage={calculateItemsPerPage()}
                        totalItems={totalItems}
                        showInfo={true}
                      />
                    </div>
                  )}
                </div>
              )}


            </div>
          </>
        )}
      />
      
      {/* 모달을 BypassLayout 밖으로 이동 */}
      <ReportModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        examData={selectedExam} 
      />
    </>
  );
}

export default ExamList;